// src/config/licenceNotice.ts
//
// Which tenants are running out of licence, and when. Drives the permanent
// countdown banner in the task pane.
//
// This is deliberately a frontend-only list rather than a backend entitlement.
// The add-in ships as one shared multi-tenant bundle, so the tenant id is the
// only signal available client-side that says which customer a user belongs to.
// It comes from the `tid` claim of the real access token, which Azure AD issues
// and the add-in never rewrites, so it cannot be spoofed from the UI.
//
// The cost of keeping it here is that moving an end date - or dropping a notice
// after a renewal - is a code change and a redeploy, not a config flip. That is
// an accepted trade for a short-lived notice; a recurring need would belong in
// the per-tenant entitlements on GET /users/me instead.

/** A tenant whose licence is ending, and the date it ends. */
export interface LicenceNotice {
  /** Azure AD tenant id - the JWT `tid` claim. */
  tenantId: string;
  /** Last day of access, as a local calendar date (YYYY-MM-DD). */
  endDate: string;
}

/**
 * Tenants currently shown the expiry banner. Empty means nobody sees it, which
 * is the normal state - the banner and everything behind it stay in place so a
 * notice can be raised again by adding one entry:
 *
 *   { tenantId: "<Azure AD tenant id>", endDate: "2026-12-31" }
 *
 * Remove an entry once the tenant renews or their access is withdrawn - a stale
 * entry keeps counting down at a customer who has already resolved it.
 */
export const LICENCE_NOTICES: readonly LicenceNotice[] = [];

export interface ResolvedLicenceNotice {
  /** Whole calendar days from today until the end date. 0 on the last day. */
  daysLeft: number;
  /** The end date at local midnight, for display. */
  endDate: Date;
}

/**
 * Parse a YYYY-MM-DD string into a LOCAL midnight Date.
 *
 * Built from its parts rather than `new Date(string)`: the Date constructor
 * reads a bare date string as UTC midnight, which is the previous day in every
 * timezone west of Greenwich - so a US user would see the countdown end a day
 * early. Returns null for anything that isn't a real calendar date.
 *
 * Exported for its own tests: a date the list rejects shows no banner at all.
 */
export function parseLocalDate(value: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);

  if (!match) return null;
  const [, year, month, day] = match;
  const date = new Date(Number(year), Number(month) - 1, Number(day));

  // Rejects overflow like 2026-02-31, which the constructor would roll forward.
  if (date.getMonth() !== Number(month) - 1 || date.getDate() !== Number(day)) {
    return null;
  }

  return date;
}

/**
 * Whole calendar days between two dates, counted midnight to midnight.
 *
 * Rounding rather than flooring the millisecond span is what makes this safe
 * across a daylight-saving change: those days are 23 or 25 hours long, so an
 * exact division would drift by a fraction of a day and truncate to the wrong
 * answer. Exported for its own tests.
 */
export function wholeDaysBetween(from: Date, to: Date): number {
  const fromMidnight = new Date(from.getFullYear(), from.getMonth(), from.getDate());
  const toMidnight = new Date(to.getFullYear(), to.getMonth(), to.getDate());

  return Math.round((toMidnight.getTime() - fromMidnight.getTime()) / 86_400_000);
}

/**
 * The licence notice for a tenant, or null if there is nothing to show.
 *
 * Null covers every "no banner" case: no tenant id, a tenant with no notice, a
 * malformed date in the list, and a date already past. The last one matters
 * because access removal is a separate manual step - if it slips, the banner
 * stops at the end date rather than counting into the negative.
 *
 * The list is a parameter rather than read from LICENCE_NOTICES directly so the
 * behaviour can be tested against a fixed notice, independent of whichever
 * tenants (usually none) are live. `now` is injectable for the same reason.
 */
export function resolveLicenceNotice(
  notices: readonly LicenceNotice[],
  tenantId: string | null | undefined,
  now: Date = new Date()
): ResolvedLicenceNotice | null {
  if (!tenantId) return null;

  const notice = notices.find((entry) => entry.tenantId === tenantId);

  if (!notice) return null;

  const endDate = parseLocalDate(notice.endDate);

  if (!endDate) return null;

  const daysLeft = wholeDaysBetween(now, endDate);

  if (daysLeft < 0) return null;

  return { daysLeft, endDate };
}
